import useEventCallback from 'mk2/hooks/useEventCallback';
import React from 'react';

type Timeout = null | number | [number, number];
type Return<TargetElementType, TriggerElementType> = [
    boolean, // true if overlay should be displayed
    (event: React.MouseEvent<TriggerElementType>) => void, // onMouseEnter
    (event: React.MouseEvent<TriggerElementType>) => void, // onMouseLeave
    React.RefObject<TargetElementType>, // target ref
    () => TargetElementType, // get target function
    () => HTMLElement // container function to render overlay in the document.body
];

/**
 * USAGE: When you need to display Tooltip or Popover overlay on mouse over
 *
 * @param timeout Number of ms to delay overlay display. You can pass number or array of two numbers
 * where first is delay for show and second is delay for hide event.
 */
function useOverlay<TargetElementType extends HTMLElement, TriggerElementType extends HTMLElement = TargetElementType>(
    timeout?: Timeout,
): Return<TargetElementType, TriggerElementType> {
    const showTimer = React.useRef<number>();
    const hideTimer = React.useRef<number>();

    const targetRef = React.useRef<TargetElementType>();
    const getTarget = React.useCallback(() => targetRef.current, []);

    const [showOverlay, setShowOverlay] = React.useState(false);

    const onMouseEnter = useEventCallback(
        (event: React.MouseEvent<TriggerElementType>) => {
            if (!timeout) {
                setShowOverlay(true);
            } else {
                clearTimeout(hideTimer.current);
                clearTimeout(showTimer.current);
                showTimer.current = window.setTimeout(
                    () => {
                        setShowOverlay(true);
                    },
                    Array.isArray(timeout) ? timeout[0] : timeout,
                );
            }
        },
        [setShowOverlay],
    );
    const onMouseLeave = useEventCallback(
        (event: React.MouseEvent<TriggerElementType>) => {
            if (!timeout) {
                setShowOverlay(false);
            } else {
                clearTimeout(hideTimer.current);
                clearTimeout(showTimer.current);
                hideTimer.current = window.setTimeout(
                    () => {
                        setShowOverlay(false);
                    },
                    Array.isArray(timeout) ? timeout[1] : timeout,
                );
            }
        },
        [setShowOverlay],
    );

    const bodyContainer = React.useCallback(() => document.body, []);

    React.useEffect(() => {
        return () => {
            if (timeout) {
                // will unmount
                clearTimeout(showTimer.current);
                clearTimeout(hideTimer.current);
            }
        };
    }, []);

    return [showOverlay, onMouseEnter, onMouseLeave, targetRef, getTarget, bodyContainer];
}

export default useOverlay;
